import axios from 'axios'

export interface RewardClaimed {
  createdAt: string
  expiredAt: string
  hash: string
  id: number
  isPending: boolean
  item: string | null
  name: string
  pictureUrl: string
  points: number
  spentPoints: number
  value: string | number
}

interface ClaimedRewardResponse {
  id: number
  date: string
  spentPoints: number
  hash: string
  reward: {
    id: number
    name: string
    pictureUrl: string
    points: number
    value: string
    canClaim: boolean
    externalId: string | null
    menus: any[]
    text: string
  }
  isPending: boolean
  item: string | null
  expiredAt: string | null
}
interface CustomerClaimedReward {
  id: number
  points: number
  isValid: boolean
  isVoucher: boolean
  couponCode: string | null
  hash: string
  expiredAt: string | null
}

export default function useCustomerRewards() {
  const { t } = useI18n()
  const notification = useNotification()

  const sortByExpiredAt = (a: RewardClaimed, b: RewardClaimed) => {
    return new Date(a?.expiredAt || '').getTime() - new Date(b?.expiredAt || '').getTime()
  }

  const claimCustomerReward = async (customerPrivateId: string, rewardId: number, storeId: number) => {
    try {
      const { data: claimedReward } = await axios.post<{ data: CustomerClaimedReward }>(
        `/customers/${customerPrivateId}/rewards/${rewardId}/claim`,
        {
          storeId
        }
      )

      notification.create('success', {
        text: t('pages.customer.customerDetailPage.reward.notifications.success.claim'),
        timeout: 5000
      })

      return claimedReward.data
    } catch {
      notification.create('error', {
        text: t('pages.customer.customerDetailPage.reward.notifications.errors.claim'),
        timeout: 5000
      })
    }
  }

  const useCustomerReward = async (rewardHash: string) => {
    try {
      const { customerPointsId } = await validateRewards(rewardHash)
      const { data } = await axios.patch(`/public/customer-points/${customerPointsId}/use`)
      if (data.data.isUsed) {
        notification.create('success', {
          text: t('pages.customer.customerDetailPage.reward.notifications.success.use'),
          timeout: 5000
        })
      }
    } catch {
      notification.create('error', {
        text: t('pages.customer.customerDetailPage.reward.notifications.errors.use'),
        timeout: 5000
      })
    }
  }

  const validateRewards = async (rewardHash: string) => {
    const { data } = await axios.patch(`/rewards/validate/${rewardHash}`)
    return { customerPointsId: data.data.id }
  }

  const requestCustomerMerchantUnusedClaimedRewards = async (customerPrivateId: string): Promise<RewardClaimed[]> => {
    const { data } = await axios.get<{ data: ClaimedRewardResponse[] }>(
      `/customers/${customerPrivateId}/rewards/claimed`,
      {
        params: {
          used: 0
        }
      }
    )

    return data.data
      .sort((a, b) => sortByExpiredAt(a, b))
      .sort((a, b) => a.spentPoints - b.spentPoints)
      .map((r) => ({
        createdAt: r.date,
        expiredAt: r.expiredAt,
        hash: r.hash,
        id: r.reward.id,
        isPending: r.isPending,
        item: r.item,
        name: r.reward.name,
        pictureUrl: r.reward.pictureUrl,
        points: r.reward.points,
        spentPoints: r.spentPoints,
        value: r.reward.value
      }))
  }

  const requestCustomerMerchantClaimableRewards = async (customerPrivateId: string) => {
    const { data } = await axios.get(`/customers/${customerPrivateId}/rewards/available`)
    return data.data
  }

  return {
    claimCustomerReward,
    requestCustomerMerchantClaimableRewards,
    requestCustomerMerchantUnusedClaimedRewards,
    useCustomerReward
  }
}
